import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plug, RefreshCw, Loader2, CheckCircle2, AlertTriangle, XCircle, Eye, Zap, Users, ReceiptText, Clock, Search,
  Link2, EyeOff, RotateCcw, PencilLine, Settings2, History, FlaskConical,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { PageLoader, LoadError, EmptyState } from "@/components/admin/LoadState";
import { Pager } from "@/components/admin/Pager";
import { usePageTitle } from "@/hooks/usePageTitle";
import { fmtDateTime, fmtNum, fmtDate } from "@/lib/format";

const STATUS: Record<string, { label: string; help: string; cls: string }> = {
  review: { label: "Por revisar", help: "Cédula inválida, repetida o de empresa: decide qué hacer.", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  linked: { label: "Enlazados", help: "Clientes de Odoo con su cuenta de Armony Contigo.", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  available: { label: "Sin cuenta aún", help: "Cédula válida: pueden registrarse en el portal.", cls: "bg-sky-50 text-navy border-sky-200" },
  archived: { label: "Archivados", help: "Archivados en Odoo: ya no pueden registrarse (las cuentas creadas siguen).", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  ignored: { label: "Ignorados", help: "Descartados a mano: la integración no los toma en cuenta.", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};
const PAGE_SIZE = 20;

const Stat = ({ icon: Icon, label, value, hint, onClick, active }: any) => (
  <button type="button" onClick={onClick} disabled={!onClick} aria-pressed={active}
    className={`card-obsidian p-4 text-left w-full transition-colors ${onClick ? "hover:bg-slate-50" : "cursor-default"} ${active ? "ring-2 ring-navy" : ""}`}>
    <p className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-600"><Icon className="w-3.5 h-3.5" aria-hidden="true" /> {label}</p>
    <p className="text-2xl font-semibold font-mono text-slate-900 mt-1">{value}</p>
    {hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
  </button>
);

// Integración con Odoo 15: estado, sincronización, simulación, reglas y bandeja de revisión.
export default function AdminOdoo() {
  usePageTitle("Integración con Odoo");
  const [data, setData] = useState<any>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState("");
  const [test, setTest] = useState<any>(null);
  const [preview, setPreview] = useState<any>(null);
  const [form, setForm] = useState<any>(null);
  const [domainText, setDomainText] = useState("");
  const [confirmMode, setConfirmMode] = useState(false);

  const load = useCallback(() => {
    setFailed(false);
    return api.get("/admin/odoo").then((r) => {
      setData(r.data);
      setForm((f: any) => f ?? r.data.settings);
      setDomainText((t) => t || JSON.stringify(r.data.settings.partner_domain));
      return r.data;
    }).catch(() => setFailed(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Mientras sincroniza, se actualiza cada pocos segundos.
  useEffect(() => {
    if (!data?.running) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [data?.running, load]);

  const runTest = async () => {
    setBusy("test"); setTest(null);
    try { setTest((await api.post("/admin/odoo/test")).data); } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const runSync = async (full: boolean) => {
    setBusy(full ? "full" : "sync");
    try {
      await api.post("/admin/odoo/sync", null, { params: { full } });
      toast.success(full ? "Sincronización completa en curso" : "Sincronización en curso");
      setTimeout(load, 800);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const runPreview = async () => {
    setBusy("preview");
    try { setPreview((await api.post("/admin/odoo/preview")).data); } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const saveSettings = async (patch: any = {}) => {
    let domain;
    try { domain = JSON.parse(domainText); } catch { toast.error("«Quién entra al programa» no es una lista válida."); return; }
    setBusy("settings");
    try {
      const body = { ...form, ...patch, partner_domain: domain, grace_days: Number(form.grace_days), punctual_tolerance_days: Number(form.punctual_tolerance_days),
        punctual_since: (patch.punctual_since ?? form.punctual_since) || null };
      const { data: saved } = await api.put("/admin/odoo/settings", body);
      setForm(saved);
      toast.success(saved.mode === "activo" && data.settings.mode !== "activo" ? "Integración activada" : "Reglas guardadas");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  if (failed) return <LoadError what="la integración con Odoo" onRetry={load} />;
  if (!data || !form) return <PageLoader />;
  const cfg = data.config;
  const canEdit = data.can_edit;
  const active = data.settings.mode === "activo";
  const last = data.runs[0];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="admin-odoo">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-serif font-medium">Integración con Odoo</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Armony Contigo lee de Odoo los clientes y sus facturas: quién puede registrarse, quién está al día y quién pagó a tiempo.
            Solo lectura: nunca escribe en Odoo. Los cambios se ven con unos minutos de retraso.
          </p>
        </div>
        <span className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full border ${active ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}
          data-testid="odoo-mode">
          {active ? <Zap className="w-4 h-4" aria-hidden="true" /> : <Eye className="w-4 h-4" aria-hidden="true" />}
          {active ? "Modo activo" : "Modo observación"}
        </span>
      </div>

      {/* Conexión */}
      <section className="card-obsidian p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex gap-3">
            <div className={`w-10 h-10 rounded-full grid place-items-center ${cfg.configured ? "bg-emerald-500/15" : "bg-amber-500/15"}`}>
              <Plug className={`w-5 h-5 ${cfg.configured ? "text-emerald-700" : "text-amber-700"}`} aria-hidden="true" />
            </div>
            <div>
              <p className="font-semibold text-slate-900">{cfg.configured ? `Conectado a ${cfg.url}` : "Falta configurar la conexión"}</p>
              <p className="text-sm text-slate-600">
                {cfg.configured ? <>Base <code>{cfg.db}</code> · usuario <code>{cfg.username}</code></>
                  : <>Configura en Railway: {cfg.missing.map((m: string) => <code key={m} className="mr-1">{m}</code>)}</>}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {data.running ? <span className="inline-flex items-center gap-1 text-navy font-medium"><Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> Sincronizando…</span>
                  : last ? <>Última sincronización: {fmtDateTime(last.started_at)} · {last.status === "ok" ? "correcta" : <span className="text-red-700">con error</span>}</>
                  : "Todavía no se ha sincronizado."}
              </p>
            </div>
          </div>
          {canEdit && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={runTest} disabled={!cfg.configured || !!busy} className="rounded-full" data-testid="odoo-test">
                {busy === "test" ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Probando" /> : <><Plug className="w-4 h-4 mr-1.5" aria-hidden="true" /> Probar conexión</>}
              </Button>
              <Button variant="outline" onClick={() => runSync(true)} disabled={!cfg.configured || !!busy || data.running} className="rounded-full">
                Sincronización completa
              </Button>
              <Button onClick={() => runSync(false)} disabled={!cfg.configured || !!busy || data.running} className="gold-gradient-bg text-white font-semibold rounded-full" data-testid="odoo-sync">
                {busy === "sync" ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Iniciando" /> : <><RefreshCw className="w-4 h-4 mr-1.5" aria-hidden="true" /> Sincronizar ahora</>}
              </Button>
            </div>
          )}
        </div>
        {last?.status === "error" && (
          <p className="flex gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" role="alert"><XCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" /> {last.error}</p>
        )}
        {test && <TestResult test={test} />}
      </section>

      {/* Resumen */}
      <section aria-label="Resumen">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={Users} label="Enlazados" value={fmtNum(data.partners.linked)} hint="clientes con cuenta" />
          <Stat icon={Users} label="Sin cuenta aún" value={fmtNum(data.partners.available)} hint="pueden registrarse" />
          <Stat icon={AlertTriangle} label="Por revisar" value={fmtNum(data.partners.review)} hint="en la bandeja" />
          <Stat icon={ReceiptText} label="Facturas" value={fmtNum(data.invoices)} hint={`${fmtNum(data.overdue_by_odoo)} ${data.overdue_by_odoo === 1 ? "cliente" : "clientes"} en mora por Odoo`} />
        </div>
      </section>

      {/* Simulación */}
      <section className="card-obsidian p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 font-semibold text-slate-900"><FlaskConical className="w-4 h-4 text-navy" aria-hidden="true" /> Qué pasaría con las reglas actuales</h2>
            <p className="text-sm text-slate-600">Con lo ya sincronizado, sin consultar Odoo ni cambiar nada. Úsalo antes de activar.</p>
          </div>
          <Button variant="outline" onClick={runPreview} disabled={!!busy} className="rounded-full" data-testid="odoo-preview">
            {busy === "preview" ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Calculando" /> : "Calcular"}
          </Button>
        </div>
        {preview && (
          <div className="grid sm:grid-cols-3 gap-4" data-testid="odoo-preview-result">
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4">
              <p className="text-2xl font-semibold font-mono">{fmtNum(preview.to_overdue)}</p>
              <p className="text-sm text-slate-700">pasarían a <strong>en mora</strong> (facturas vencidas hace más de {data.settings.grace_days} días)</p>
              {preview.examples.to_overdue.length > 0 && <p className="text-xs text-slate-500 mt-1">Ej.: {preview.examples.to_overdue.join(", ")}</p>}
            </div>
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4">
              <p className="text-2xl font-semibold font-mono">{fmtNum(preview.to_active)}</p>
              <p className="text-sm text-slate-700">volverían a <strong>al día</strong> (los había puesto en mora Odoo y ya pagaron)</p>
            </div>
            <div className="rounded-xl bg-sky-50 border border-sky-200 p-4">
              <p className="text-2xl font-semibold font-mono">{fmtNum(preview.punctual_total)}</p>
              <p className="text-sm text-slate-700">facturas pagadas a tiempo en lo sincronizado. Se premian las emitidas desde la activación{data.settings.punctual_since ? ` (${fmtDate(data.settings.punctual_since)})` : ""}.</p>
            </div>
          </div>
        )}
      </section>

      {/* Reglas */}
      <section className="card-obsidian p-5 space-y-5">
        <div>
          <h2 className="flex items-center gap-2 font-semibold text-slate-900"><Settings2 className="w-4 h-4 text-navy" aria-hidden="true" /> Reglas</h2>
          <p className="text-sm text-slate-600">{canEdit ? "Las decide Armony. Se aplican en la próxima sincronización." : "Solo el administrador principal puede cambiarlas."}</p>
        </div>
        <div className="rounded-xl border border-slate-200 p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="max-w-2xl">
            <p className="font-medium text-slate-900">{active ? "Modo activo" : "Modo observación"}</p>
            <p className="text-sm text-slate-600">
              {active ? "Odoo alimenta la base de cédulas autorizadas, pone en mora o al día a los clientes, entrega los puntos por pago puntual y muestra «Mis facturas» en el portal."
                : "Sincroniza y muestra qué pasaría, pero no cambia a ningún cliente ni la base de cédulas."}
            </p>
          </div>
          {canEdit && (
            <Button variant={active ? "outline" : "default"} onClick={() => setConfirmMode(true)} disabled={!!busy}
              className={active ? "rounded-full" : "gold-gradient-bg text-white font-semibold rounded-full"} data-testid="odoo-toggle-mode">
              {active ? "Volver a observación" : "Activar integración"}
            </Button>
          )}
        </div>
        <fieldset disabled={!canEdit} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label htmlFor="o-grace" className="text-sm text-slate-700">Días de gracia</Label>
            <Input id="o-grace" type="number" min={0} max={120} value={form.grace_days} onChange={(e: any) => setForm({ ...form, grace_days: e.target.value })} className="mt-1.5" />
            <p className="text-xs text-slate-500 mt-1">Después del vencimiento, antes de «en mora».</p>
          </div>
          <div>
            <Label htmlFor="o-tol" className="text-sm text-slate-700">Tolerancia del pago puntual (días)</Label>
            <Input id="o-tol" type="number" min={0} max={30} value={form.punctual_tolerance_days} onChange={(e: any) => setForm({ ...form, punctual_tolerance_days: e.target.value })} className="mt-1.5" />
            <p className="text-xs text-slate-500 mt-1">0 = hasta el día del vencimiento.</p>
          </div>
          <div>
            <Label htmlFor="o-since" className="text-sm text-slate-700">Traer facturas desde</Label>
            <Input id="o-since" type="date" value={form.invoices_since} onChange={(e: any) => setForm({ ...form, invoices_since: e.target.value })} className="mt-1.5" />
          </div>
          <div>
            <Label htmlFor="o-psince" className="text-sm text-slate-700">Premiar pagos puntuales desde</Label>
            <Input id="o-psince" type="date" value={form.punctual_since || ""} onChange={(e: any) => setForm({ ...form, punctual_since: e.target.value })} className="mt-1.5" />
            <p className="text-xs text-slate-500 mt-1">Vacío: desde que se active.</p>
          </div>
        </fieldset>
        <details className="rounded-lg border border-slate-200 px-4 py-3">
          <summary className="cursor-pointer text-sm text-slate-700">Avanzado: quién entra al programa y campo de la cédula</summary>
          <fieldset disabled={!canEdit} className="grid sm:grid-cols-[1fr_200px] gap-4 mt-3">
            <div>
              <Label htmlFor="o-domain" className="text-sm text-slate-700">Clientes de Odoo que entran (dominio de Odoo)</Label>
              <Textarea id="o-domain" rows={2} value={domainText} onChange={(e: any) => setDomainText(e.target.value)} className="mt-1.5 font-mono text-xs" />
              <p className="text-xs text-slate-500 mt-1">Por defecto, los que tienen facturas de cliente: <code>[["customer_rank", "&gt;", 0]]</code>. Ej. por etiqueta: <code>[["category_id.name", "=", "Armony Contigo"]]</code>.</p>
            </div>
            <div>
              <Label htmlFor="o-field" className="text-sm text-slate-700">Campo de la cédula</Label>
              <Input id="o-field" value={form.cedula_field} onChange={(e: any) => setForm({ ...form, cedula_field: e.target.value })} className="mt-1.5 font-mono" />
            </div>
          </fieldset>
        </details>
        {canEdit && (
          <div className="flex justify-end">
            <Button onClick={() => saveSettings()} disabled={!!busy} className="rounded-full" variant="outline" data-testid="odoo-save-settings">
              {busy === "settings" ? <Loader2 className="w-4 h-4 animate-spin" aria-label="Guardando" /> : "Guardar reglas"}
            </Button>
          </div>
        )}
      </section>

      <PartnersTray onChanged={load} counts={data.partners} />

      {/* Historial */}
      <section className="card-obsidian overflow-hidden">
        <h2 className="flex items-center gap-2 px-5 py-3 border-b border-slate-200 font-semibold text-slate-900"><History className="w-4 h-4 text-navy" aria-hidden="true" /> Sincronizaciones recientes</h2>
        {data.runs.length === 0 ? <p className="px-5 py-6 text-sm text-slate-600">Todavía no hay sincronizaciones.</p> : (
          <ul className="divide-y divide-slate-100 text-sm">
            {data.runs.map((r: any) => (
              <li key={r.id} className="px-5 py-3 flex flex-wrap items-center gap-x-4 gap-y-1">
                {r.status === "ok" ? <CheckCircle2 className="w-4 h-4 text-emerald-600" aria-label="Correcta" />
                  : r.status === "error" ? <XCircle className="w-4 h-4 text-red-600" aria-label="Con error" />
                  : <Loader2 className="w-4 h-4 animate-spin text-navy" aria-label="En curso" />}
                <span className="text-slate-900">{fmtDateTime(r.started_at)}</span>
                <span className="text-slate-500">{r.kind} · {r.trigger === "cron" ? "automática" : `manual${r.actor ? ` (${r.actor})` : ""}`}</span>
                {r.status === "ok" && <span className="text-slate-600">{fmtNum(r.stats?.partners?.received)} clientes y {fmtNum(r.stats?.invoices?.received)} facturas leídos
                  {r.stats?.evaluation?.applied ? ` · ${fmtNum(r.stats.evaluation.to_overdue)} a mora, ${fmtNum(r.stats.evaluation.to_active)} al día, ${fmtNum(r.stats.evaluation.punctual_granted)} pagos puntuales premiados` : ""}</span>}
                {r.error && <span className="text-red-700">{r.error}</span>}
              </li>
            ))}
          </ul>
        )}
      </section>

      <ConfirmDialog
        open={confirmMode}
        onOpenChange={setConfirmMode}
        danger={!active}
        title={active ? "¿Volver al modo observación?" : "¿Activar la integración con Odoo?"}
        description={active
          ? <p>Se dejan de aplicar cambios a los clientes. Los que Odoo puso en mora se quedan así hasta que el equipo los cambie o se reactive.</p>
          : <div className="space-y-2">
              <p>Desde la próxima sincronización:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Las cédulas de Odoo se suman a la base de cédulas autorizadas y las archivadas dejan de poder registrarse.</li>
                <li>Los clientes con facturas vencidas hace más de {data.settings.grace_days} días pasan a «en mora» y vuelven solos al día cuando pagan.</li>
                <li>Las facturas pagadas a tiempo desde hoy reciben los puntos de la regla automática «Paga su factura a tiempo».</li>
                <li>Los clientes ven «Mis facturas» en el portal.</li>
              </ul>
              <p>Recomendado: revisa la simulación y la bandeja de revisión antes.</p>
            </div>}
        confirmLabel={active ? "Volver a observación" : "Activar"}
        onConfirm={async () => saveSettings({ mode: active ? "observacion" : "activo" })}
      />
    </div>
  );
}

function TestResult({ test }: { test: any }) {
  if (!test.ok) return <p className="flex gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" role="alert"><XCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" /> {test.error}</p>;
  const labels: Record<string, string> = { "res.partner": "Clientes (res.partner)", "account.move": "Facturas (account.move)", "sale.subscription": "Suscripciones (sale.subscription)" };
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm space-y-1" role="status" data-testid="odoo-test-result">
      <p className="font-medium text-emerald-800 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" aria-hidden="true" /> Conexión correcta · Odoo {test.version} · {fmtNum(test.partners_in_program ?? 0)} clientes entran al programa</p>
      {Object.entries(test.models).map(([m, r]: any) => (
        <p key={m} className={r.ok && !(r.missing || []).length ? "text-slate-700" : "text-amber-800"}>
          {r.ok ? (r.missing?.length ? "⚠" : "✓") : "✕"} {labels[m] || m}
          {r.missing?.length ? `: faltan campos ${r.missing.join(", ")}` : ""}{r.error ? `: ${r.error}` : ""}
        </p>
      ))}
    </div>
  );
}

function PartnersTray({ onChanged, counts }: { onChanged: () => void; counts: Record<string, number> }) {
  const [status, setStatus] = useState("review");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<any>(null);
  const [linking, setLinking] = useState<any>(null);
  const [fixing, setFixing] = useState<any>(null);
  const [busy, setBusy] = useState("");

  // countsKey: al terminar una sincronización cambian los totales y la lista se vuelve a pedir.
  const countsKey = JSON.stringify(counts);
  const load = useCallback(() => {
    api.get("/admin/odoo/partners", { params: { status, q: q.trim() || undefined, page, page_size: PAGE_SIZE } })
      .then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [status, q, page, countsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const resolve = async (p: any, body: any, ok: string) => {
    setBusy(`${p.odoo_id}`);
    try {
      await api.post(`/admin/odoo/partners/${p.odoo_id}/resolve`, body);
      toast.success(ok);
      setLinking(null); setFixing(null);
      load(); onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  return (
    <section className="card-obsidian overflow-hidden" aria-labelledby="tray-title">
      <div className="px-5 pt-4 pb-3 border-b border-slate-200 space-y-3">
        <h2 id="tray-title" className="font-semibold text-slate-900">Clientes de Odoo</h2>
        <div className="flex flex-wrap items-center gap-2">
          {Object.entries(STATUS).map(([k, s]) => (
            <button key={k} type="button" onClick={() => { setStatus(k); setPage(1); }} aria-pressed={status === k}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border ${status === k ? "bg-navy text-white border-navy" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}
              data-testid={`odoo-tray-${k}`}>
              {s.label} · {fmtNum(counts[k] || 0)}
            </button>
          ))}
          <div className="relative ml-auto w-full sm:w-64">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
            <Input value={q} onChange={(e: any) => { setQ(e.target.value); setPage(1); }} placeholder="Nombre, documento o correo" aria-label="Buscar cliente de Odoo" className="pl-9 h-9" autoComplete="off" />
          </div>
        </div>
        <p className="text-xs text-slate-500">{STATUS[status].help}</p>
      </div>
      {!data ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-navy" aria-label="Cargando" /></div>
        : data.items.length === 0 ? <div className="p-6"><EmptyState icon={status === "review" ? CheckCircle2 : Users}>{status === "review" ? "No hay nada por revisar." : "No hay clientes en este grupo."}</EmptyState></div> : (
        <>
          <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Clientes de Odoo">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="border-b border-slate-200 text-slate-600 text-xs uppercase tracking-wider">
                  <th scope="col" className="text-left font-medium px-5 py-2.5">Cliente en Odoo</th>
                  <th scope="col" className="text-left font-medium px-5 py-2.5">Documento</th>
                  <th scope="col" className="text-left font-medium px-5 py-2.5">{status === "review" ? "Motivo" : "Cuenta en Armony Contigo"}</th>
                  <th scope="col" className="px-5 py-2.5"><span className="sr-only">Acciones</span></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((p: any) => (
                  <tr key={p.odoo_id} className="border-b border-slate-100 align-top" data-testid="odoo-partner-row">
                    <td className="px-5 py-3">
                      <p className="font-medium text-slate-900">{p.name || "Sin nombre"}</p>
                      <p className="text-xs text-slate-500">#{p.odoo_id}{p.email ? ` · ${p.email}` : ""}</p>
                    </td>
                    <td className="px-5 py-3">
                      <p className="font-mono text-slate-800">{p.vat || "—"}</p>
                      {p.cedula && p.cedula !== p.vat && <p className="text-xs text-slate-500">Cédula: <span className="font-mono">{p.cedula}</span>{p.cedula_override ? " (corregida)" : ""}</p>}
                      {p.id_type && <p className="text-xs text-slate-500">{p.id_type}</p>}
                    </td>
                    <td className="px-5 py-3">
                      {status === "review" || status === "archived" ? <p className="text-amber-800">{p.issue}</p>
                        : p.user ? <><p className="text-slate-900">{p.user.name}</p><p className="text-xs text-slate-500">{p.user.email}</p></>
                        : <span className="text-slate-500">—</span>}
                      {p.resolved_by && <p className="text-xs text-slate-500 mt-0.5">Decidido por {p.resolved_by}</p>}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {(status === "review" || status === "available") && (
                          <Button size="sm" variant="outline" className="h-8" onClick={() => setLinking(p)} disabled={!!busy}><Link2 className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Enlazar</Button>
                        )}
                        {status === "review" && (
                          <Button size="sm" variant="outline" className="h-8" onClick={() => setFixing(p)} disabled={!!busy}><PencilLine className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Corregir cédula</Button>
                        )}
                        {status !== "ignored" && status !== "archived" && (
                          <Button size="sm" variant="ghost" className="h-8 text-slate-600" disabled={!!busy}
                            onClick={() => resolve(p, { action: "ignore" }, "Cliente ignorado")}><EyeOff className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Ignorar</Button>
                        )}
                        {(status === "ignored" || (status === "linked" && p.resolved_by)) && (
                          <Button size="sm" variant="ghost" className="h-8 text-slate-600" disabled={!!busy}
                            onClick={() => resolve(p, { action: "restore" }, "Volvió a revisión automática")}><RotateCcw className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Deshacer</Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} pageSize={PAGE_SIZE} total={data.total} onPage={setPage} />
        </>
      )}

      <LinkDialog partner={linking} busy={!!busy} onClose={() => setLinking(null)}
        onLink={(u: any) => resolve(linking, { action: "link", user_id: u.id }, `Enlazado con ${u.name}`)} />
      <FixDialog partner={fixing} busy={!!busy} onClose={() => setFixing(null)}
        onSave={(cedula: string) => resolve(fixing, { action: "cedula", cedula }, "Cédula corregida")} />
    </section>
  );
}

function LinkDialog({ partner, busy, onClose, onLink }: any) {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState<any[]>([]);
  useEffect(() => { if (partner) setQ(partner.cedula || partner.vat || partner.name || ""); }, [partner]);
  useEffect(() => {
    if (!partner || q.trim().length < 2) { setUsers([]); return; }
    const t = setTimeout(() => api.get("/admin/users/search", { params: { q: q.trim(), page_size: 8 } })
      .then((r) => setUsers(r.data.items || [])).catch(() => setUsers([])), 250);
    return () => clearTimeout(t);
  }, [q, partner]);
  return (
    <Dialog open={!!partner} onOpenChange={(o: boolean) => !o && !busy && onClose()}>
      <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Enlazar con una cuenta</DialogTitle>
          <DialogDescription className="text-slate-600">«{partner?.name}» en Odoo. Busca la cuenta de Armony Contigo que corresponde a esta persona.</DialogDescription>
        </DialogHeader>
        <Input value={q} onChange={(e: any) => setQ(e.target.value)} placeholder="Cédula, nombre o correo" aria-label="Buscar cuenta" autoComplete="off" />
        <ul className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
          {users.length === 0 ? <li className="py-4 text-sm text-slate-500">{q.trim().length < 2 ? "Escribe para buscar." : "No hay cuentas que coincidan."}</li>
            : users.map((u) => (
              <li key={u.id} className="py-2 flex items-center justify-between gap-3">
                <div><p className="text-sm font-medium text-slate-900">{u.name}</p><p className="text-xs text-slate-500">{u.cedula || "sin cédula"} · {u.email}</p></div>
                <Button size="sm" onClick={() => onLink(u)} disabled={busy} className="h-8 rounded-full">Enlazar</Button>
              </li>
            ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

function FixDialog({ partner, busy, onClose, onSave }: any) {
  const [v, setV] = useState("");
  useEffect(() => { if (partner) setV(partner.cedula_override || ""); }, [partner]);
  return (
    <Dialog open={!!partner} onOpenChange={(o: boolean) => !o && !busy && onClose()}>
      <DialogContent className="bg-obsidian-card border-slate-200 text-slate-900">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Corregir la cédula</DialogTitle>
          <DialogDescription className="text-slate-600">
            En Odoo dice «{partner?.vat || "vacío"}». La corrección vale solo en Armony Contigo; para corregirla en Odoo, hazlo allá y se actualizará sola.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label htmlFor="fix-cedula" className="text-sm text-slate-700">Cédula correcta</Label>
          <Input id="fix-cedula" value={v} onChange={(e: any) => setV(e.target.value)} inputMode="numeric" maxLength={20} className="mt-1.5 font-mono" autoComplete="off" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-full">Cancelar</Button>
          <Button onClick={() => onSave(v.trim())} disabled={busy || !v.trim()} className="gold-gradient-bg text-white font-semibold rounded-full">Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
